/**
 * Standardized error handling across all services
 */

export class ServiceError extends Error {
  public readonly code: string;
  public readonly originalError?: unknown;
  
  constructor(message: string, code: string, originalError?: unknown) {
    super(message);
    this.name = 'ServiceError';
    this.code = code;
    this.originalError = originalError;
  }
}

export enum ErrorCode {
  AUTHENTICATION = 'auth/not-authenticated',
  PERMISSION = 'auth/permission-denied',
  NOT_FOUND = 'data/not-found',
  VALIDATION = 'data/validation-error',
  DATABASE = 'data/database-error',
  NETWORK = 'network/connection-error',
  UNEXPECTED = 'app/unexpected-error',
}

/**
 * Handles errors from services in a standard way
 * @param error The original error
 * @param defaultMessage A default message to use if one cannot be extracted from the error
 * @returns A standardized ServiceError
 */
export function handleServiceError(error: unknown, defaultMessage: string): ServiceError {
  // Handle Supabase errors
  if (error && typeof error === 'object' && 'code' in error) {
    // Authentication errors
    if (error.code === 'PGRST301' || 
        ('message' in error && typeof error.message === 'string' && error.message.includes('JWT'))) {
      return new ServiceError(
        'Authentication required', 
        ErrorCode.AUTHENTICATION, 
        error
      );
    }
    
    // Permission errors
    if (error.code === 'PGRST401' || error.code === '42501') {
      return new ServiceError(
        'You do not have permission to perform this action', 
        ErrorCode.PERMISSION, 
        error
      );
    }
    
    // Not found errors
    if (error.code === 'PGRST116') {
      return new ServiceError(
        'The requested resource was not found', 
        ErrorCode.NOT_FOUND, 
        error
      );
    }
    
    // Database errors
    if (typeof error.code === 'string' && error.code.startsWith('P')) {
      return new ServiceError(
        'A database error occurred', 
        ErrorCode.DATABASE, 
        error
      );
    }
  }
  
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new ServiceError(
      'Network error: Please check your connection', 
      ErrorCode.NETWORK, 
      error
    );
  }
  
  // If it's already a ServiceError, just return it
  if (error instanceof ServiceError) {
    return error;
  }
  
  // Handle unknown errors
  const message =
    error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message
      ? error.message
      : defaultMessage;
  return new ServiceError(message, ErrorCode.UNEXPECTED, error);
}

/**
 * Formats error messages for display to users
 * @param error The error to format
 * @returns A user-friendly error message
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof ServiceError) {
    return error.message;
  }
  
  if (error && typeof error === 'object' && 'message' in error) {
    // Clean up common error messages
    const message = String(error.message);
    
    // Remove technical details
    if (message.includes('TypeError')) {
      return 'A technical error occurred. Please try again.';
    }
    
    // Clean up Supabase error messages
    if (message.includes('duplicate key')) {
      return 'This record already exists.';
    }
    
    return message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}